import {
  AdminDatePickerInputModal,
  AdminInput,
  AdminSelect,
  AdminSwitch,
  Box,
  CollapsibleComponent,
  Div,
  H5,
  useMeasuredHeight,
} from '@vgrubs/components';
import {
  CUSTOM_REPEAT,
  REPEAT_PRESETS,
  type AnnouncementDraft,
  type UpdateAnnouncementDraft,
} from '../helpers/announcementPayload';
import { ToggleRow } from './AnnouncementScheduleStep.styled';

type Props = {
  draft: AnnouncementDraft;
  onChange: UpdateAnnouncementDraft;
};

export const AnnouncementScheduleStep = ({ draft, onChange }: Props) => {
  const range = useMeasuredHeight<HTMLDivElement>();
  const repeat = useMeasuredHeight<HTMLDivElement>();
  const custom = useMeasuredHeight<HTMLDivElement>();

  return (
    <Div $flex $column $gap="24px" $mt="20">
      {/* The rows read as one setting, so they sit as close together as the
          rows on the list page. Flat boxes: the modal is already a raised
          surface, same as the audience groups a step earlier. */}
      <Div $flex $column $gap="8px">
        <ToggleBox
          label="Active"
          checked={draft.active}
          onToggle={() => onChange('active', !draft.active)}
        />

        <ToggleBox
          label="Show in date range"
          checked={draft.showInRange}
          onToggle={() => onChange('showInRange', !draft.showInRange)}
        >
          <CollapsibleComponent
            expanded={draft.showInRange}
            elementHeight={range.height}
          >
            {/* The bottom padding is measured with the content so the gap
                animates too; selection is re-enabled because the collapsible
                turns it off for everything inside. */}
            <div
              ref={range.ref}
              style={{ paddingBottom: '20px', userSelect: 'text' }}
            >
              <AdminDatePickerInputModal
                label="From - To"
                mode="range"
                enableTime
                value={draft.dateRange}
                onChange={(dateRange: Date[]) =>
                  onChange('dateRange', dateRange)
                }
                $textCenter
              />
            </div>
          </CollapsibleComponent>
        </ToggleBox>

        <ToggleBox
          label="Repeat"
          checked={draft.repeats}
          onToggle={() => onChange('repeats', !draft.repeats)}
        >
          <CollapsibleComponent
            expanded={draft.repeats}
            elementHeight={repeat.height}
          >
            <div
              ref={repeat.ref}
              style={{ paddingBottom: '20px', userSelect: 'text' }}
            >
              {/* Named intervals first; raw minutes are the escape hatch, not
                  the front door. The outer height is observed, so the custom
                  input sliding open below grows this collapsible with it. */}
              <AdminSelect
                label="How often"
                value={draft.repeatPreset}
                onChange={(e) => onChange('repeatPreset', e.target.value)}
                options={
                  <>
                    {REPEAT_PRESETS.map((preset) => (
                      <option key={preset.minutes} value={preset.minutes}>
                        {preset.label}
                      </option>
                    ))}
                    <option value={CUSTOM_REPEAT}>Custom...</option>
                  </>
                }
                $textCenter
              />
              <CollapsibleComponent
                expanded={draft.repeatPreset === CUSTOM_REPEAT}
                elementHeight={custom.height}
              >
                <div
                  ref={custom.ref}
                  style={{ paddingTop: '16px', userSelect: 'text' }}
                >
                  <AdminInput
                    label="Interval (minutes)"
                    placeholder="e.g. 90 for every 1.5 hours"
                    value={draft.repeatInterval}
                    /* Digits only - a stray letter must not turn into NaN. */
                    onChange={(e) =>
                      onChange(
                        'repeatInterval',
                        e.target.value.replace(/[^0-9]/g, '')
                      )
                    }
                    $textCenter
                  />
                </div>
              </CollapsibleComponent>
            </div>
          </CollapsibleComponent>
        </ToggleBox>
      </Div>
    </Div>
  );
};

/**
 * A switch row that can grow content underneath.
 *
 * The row keeps the closed card's full 70px height even when expanded, so
 * opening one adds below rather than re-centering what was already there -
 * the label stays exactly where it was when the card was closed.
 */
const ToggleBox = ({
  label,
  checked,
  onToggle,
  children,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) => (
  /* No vertical padding here, and none on a phone either - the row below
     carries the height instead, which is what keeps the label on the box's
     centre line rather than under its top padding. */
  <Box $p="0 20px" $pSM="0 16px" $boxNoShadow>
    <ToggleRow>
      <H5 $m="0">{label}</H5>
      <AdminSwitch checked={checked} onChange={onToggle} />
    </ToggleRow>
    {children}
  </Box>
);
